import {
  Box,
  Divider,
  Select,
  Typography,
  MenuItem,
  Avatar,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import vajralog from "../assets/images/vajra_logo.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { sidebarElementsGroupAdmin } from "../utils/constant";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useUser } from "../context/AuthContext";
import { get } from "../utils/api";
import useAuthStore from "../store/ContextUser";
import styles from "../styles/layout/groupAdminSidebar.module.css";
import { clsx } from "clsx";

const GroupAdminSidebar = () => {
  const { user, module } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCompany, setSelectedCompany } = useAuthStore();

  const [openSections, setOpenSections] = useState({});
  const [companyList, SetcompanyList] = useState([]);

  const toggleSection = (heading) => {
    setOpenSections((prev) => ({
      ...prev,
      [heading]: !prev[heading],
    }));
  };

  const filterSidebar = (items) => {
    return items
      .map((item) => {
        // If moduleName directly matches
        if (
          item.heading === "Overview" ||
          item.heading === "Support" ||
          item.heading === "FAQ" ||
          item.heading === "Settings"
        ) {
          return item;
        }
        if (item.moduleName && module.includes(item.moduleName)) {
          return item;
        }

        // Handle subData (nested children)
        if (item.subData) {
          const filteredSub = filterSidebar(item.subData);
          if (filteredSub.length > 0) {
            return { ...item, subData: filteredSub };
          }
        }

        // Handle subChildren
        if (item.subChildren) {
          const filteredSubChildren = filterSidebar(item.subChildren);
          if (filteredSubChildren.length > 0) {
            return { ...item, subChildren: filteredSubChildren };
          }
        }

        // Handle subsubChildren
        if (item.subsubChildren) {
          const filteredSubSubChildren = item.subsubChildren.filter((subsub) =>
            module.includes(subsub.moduleName)
          );
          if (filteredSubSubChildren.length > 0) {
            return { ...item, subsubChildren: filteredSubSubChildren };
          }
        }

        return null;
      })
      .filter(Boolean); // Remove null entries
  };

  // Helper to recursively find which sections should be open for a given path
  function getOpenSectionsForPath(elements, pathname) {
    const open = {};
    elements.forEach((section) => {
      // Top-level
      if (section.collapse && section.subData) {
        section.subData.forEach((item) => {
          if (isPathMatchOrNested(item, pathname)) {
            open[section.heading] = true;
            if (item.subHeading) {
              open[item.subHeading] = true;
            }
          }
        });
      }
      // Top-level direct path
      if (section.path && pathname.startsWith(section.path)) {
        open[section.heading] = true;
      }
    });
    return open;
  }

  // Helper to check if a subData/subChildren/subsubChildren item matches the path
  function isPathMatchOrNested(item, pathname) {
    // Direct path or url
    if (
      (item.path && pathname.startsWith(item.path)) ||
      (item.url && pathname.startsWith(item.url))
    ) {
      return true;
    }
    // subcollapse (subChildren)
    if (item.subcollapse && item.subChildren) {
      return item.subChildren.some((sub) => isPathMatchOrNested(sub, pathname));
    }
    // collapse (subsubChildren)
    if (item.collapse && item.subsubChildren) {
      return item.subsubChildren.some((sub) =>
        isPathMatchOrNested(sub, pathname)
      );
    }
    return false;
  }

  useEffect(() => {
    get(`/api/group/company/company/list`)
      .then((res) => {
        SetcompanyList(res.data.data);
        setSelectedCompany(
          selectedCompany ? selectedCompany : res.data.data[0]
        );
      })
      .catch((err) => {
        console.log(err, "err ");
      });
  }, [user?.groupManager, setSelectedCompany]);

  useEffect(() => {
    // Use the filtered sidebar for the current user
    const elements = user?.subGroupManager
      ? filterSidebar(sidebarElementsGroupAdmin)
      : sidebarElementsGroupAdmin;
    setOpenSections(getOpenSectionsForPath(elements, location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.subGroupManager]);


  const handleChange = (e) => {
    setSelectedCompany(e.target.value);
    companyList.find((company) => company._id === e.target.value);
  };

  const renderSidebarItems = (elements) => {
    return elements.map((section, index) => (
      <div key={index} className={styles.sidebarSection}>
        {section.heading && (
          <div className={styles.sectionHeadingRow}>
            <div
              className={clsx(
                styles.sectionHighlight,
                location.pathname.startsWith(section.path) &&
                  styles.sectionHighlightActive
              )}
            ></div>
            <a
              href={section.path}
              onClick={(e) => {
                if (section.collapse) {
                  e.preventDefault();
                  toggleSection(section.heading);
                }
              }}
              className={clsx(
                styles.sectionLink,
                !section?.collapse &&
                  location.pathname.startsWith(section.path) &&
                  styles.sectionLinkActive
              )}
            >
              <Box
                component="img"
                src={section.icon}
                alt="icon"
                sx={{
                  width: "20px",
                  height: "20px",
                  marginRight: "8px",
                  filter:
                    !section?.collapse &&
                    location.pathname.startsWith(section.path)
                      ? "invert(1)"
                      : "invert(0)",
                }}
              />
              <span className={styles.sectionHeadingText}>
                {section.heading}
              </span>
              {section.collapse &&
                (openSections[section.heading] ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                ))}
            </a>
          </div>
        )}

        {section.subData &&
          (!section.collapse || openSections[section.heading]) &&
          section.subData.map((item) => (
            <div
              key={item.path || item.url}
              className={styles.subDataContainer}
            >
              {item.subHeading ? (
                item.subcollapse ? (
                  // If subcollapse is true, it acts as a collapsible section
                  <div
                    onClick={() => toggleSection(item.subHeading)}
                    className={styles.subCollapseSection}
                  >
                    <div className={styles.subCollapseHeader}>
                      <Box
                        component="img"
                        src={item.icon}
                        alt="img"
                        sx={{
                          width: "20px",
                          height: "20px",
                          marginRight: "8px",
                          filter: location.pathname.startsWith(
                            item.path || item.url
                          )
                            ? "invert(1)"
                            : "invert(0)",
                        }}
                      />
                      {item.subHeading}
                    </div>
                    {openSections[item.subHeading] ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </div>
                ) : (
                  // If subcollapse is false, make it a clickable link
                  <a
                    href={item.path || item.url}
                    className={clsx(
                      styles.subDataLink,
                      location.pathname.startsWith(item.path || item.url) &&
                        styles.subDataLinkActive
                    )}
                  >
                    <Box
                      component="img"
                      src={item.icon}
                      alt="img"
                      sx={{
                        width: "20px",
                        height: "20px",
                        marginRight: "8px",
                        filter: location.pathname.startsWith(
                          item.path || item.url
                        )
                          ? "invert(1)"
                          : "invert(0)",
                      }}
                    />
                    <Typography
                      className={clsx(
                        styles.subDataText,
                        location.pathname.startsWith(item.path || item.url) &&
                          styles.subDataTextActive
                      )}
                    >
                      {item.subHeading}
                    </Typography>
                  </a>
                )
              ) : (
                <a
                  href={item.path || item.url}
                  className={clsx(
                    styles.subDataLink,
                    location.pathname.startsWith(item.path || item.url) &&
                      styles.subDataLinkActive,
                    styles.subDataLinkNoHeading
                  )}
                >
                  <Box
                    component="img"
                    src={item.icon}
                    alt="icon"
                    sx={{
                      width: "20px",
                      height: "20px",
                      marginRight: "8px",
                      filter: location.pathname.startsWith(
                        item.path || item.url
                      )
                        ? "invert(1)"
                        : "invert(0)",
                    }}
                  />
                  <Typography
                    className={clsx(
                      styles.subDataText,
                      location.pathname.startsWith(item.path || item.url) &&
                        styles.subDataTextActive
                    )}
                  >
                    {item.primary}
                  </Typography>
                </a>
              )}

              {item.subChildren &&
                openSections[item.subHeading] &&
                item.subChildren.map((subItem) => (
                  <a
                    key={subItem.path}
                    href={subItem.path}
                    className={clsx(
                      styles.subChildLink,
                      location.pathname.startsWith(subItem.path) &&
                        styles.subChildLinkActive
                    )}
                  >
                    <Box
                      component="img"
                      src={subItem.icon}
                      alt={"icon"}
                      sx={{
                        width: "20px",
                        height: "20px",
                        marginRight: "8px",
                        filter: location.pathname.startsWith(subItem.path)
                          ? "invert(1)"
                          : "invert(0)",
                      }}
                    />
                    <Typography
                      className={clsx(
                        styles.subChildText,
                        location.pathname.startsWith(subItem.path) &&
                          styles.subChildTextActive
                      )}
                    >
                      {subItem?.primary}
                    </Typography>
                    <div key={index} className={styles.subSubContainer}>
                      <div
                        className={styles.subSubHeader}
                        onClick={() => toggleSection(subItem.subHeading)}
                      >
                        <span className={styles.subSubHeadingText}>
                          {subItem?.subHeading}
                        </span>
                        {subItem?.collapse &&
                          (openSections[subItem?.subHeading] ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          ))}
                      </div>
                      {subItem?.subsubChildren &&
                        openSections[subItem.subHeading] &&
                        subItem?.subsubChildren?.map((subItems) => (
                          <a
                            key={subItems.path}
                            href={subItems.path}
                            className={clsx(
                              styles.subSubLink,
                              location.pathname.startsWith(subItems.path) &&
                                styles.subSubLinkActive
                            )}
                          >
                            <Box
                              component="img"
                              src={subItems.icon}
                              alt={"icon"}
                              sx={{
                                width: "20px",
                                height: "20px",
                                marginLeft: "8px",
                                filter: location.pathname.startsWith(
                                  subItems.path
                                )
                                  ? "invert(0)"
                                  : "invert(1)",
                              }}
                            />
                            <Typography
                              className={clsx(
                                styles.subSubText,
                                location.pathname.startsWith(subItems.path) &&
                                  styles.subSubTextActive
                              )}
                            >
                              {subItems?.text}
                            </Typography>
                          </a>
                        ))}
                    </div>
                  </a>
                ))}
            </div>
          ))}
      </div>
    ));
  };

  return (
    <>
      <Box className={styles.sidebarRoot}>
        <Box>
          <Box className={styles.logoContainer}>
            <img
              src={vajralog}
              alt="Nirantara Logo"
              className={styles.logoImage}
              onClick={() => navigate("/dashboard")}
            />
          </Box>
          <Divider />
          <div className={styles.sidebarContent}>
            <Box className={styles.companySelectBox}>
              <Typography className={styles.companySelectLabel}>
                Select Company
              </Typography>
              <Select
                value={selectedCompany} // default selected value
                onChange={handleChange}
                displayEmpty
                className={styles.companySelect}
                renderValue={(selected) => (
                  <Box className={styles.companySelectRenderValue}>
                    <Avatar
                      src={selected?.companyImage}
                      alt="Nirantara"
                      className={styles.companyAvatar}
                    />
                    <Typography
                      variant="body1"
                      className={styles.companyNameText}
                    >
                      {selected?.companyName}
                    </Typography>
                  </Box>
                )}
              >
                {companyList.map((item) => {
                  return (
                    <MenuItem
                      value={item}
                      selected={selectedCompany?._id === item?._id}
                    >
                      {item?.companyName}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
            {renderSidebarItems(
              user?.subGroupManager
                ? filterSidebar(sidebarElementsGroupAdmin)
                : sidebarElementsGroupAdmin
            )}
          </div>
        </Box>
      </Box>
    </>
  );
};

export default GroupAdminSidebar;
